import { useEffect, useMemo, useState } from "react";

import { Drawer, Flex, Group, NumberInput, Stack, Switch } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { DisabledReasonButton, NO_CHANGES_REASON } from "@/components/DisabledReason";
import { TextWrapper } from "@/components/TextWrapper";
import { replaceFormSnapshotForDrawerDismiss } from "@/settings/utils/replace-form-snapshot-for-drawer-dismiss";

const currencyInputStyles = {
  label: {
    marginBottom: 8,
  },
  wrapper: {
    display: "flex",
    alignItems: "center",
  },
  section: {
    width: 49,
    height: 36,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "var(--mantine-color-gray-1)",
    borderRadius: "2px 0 0 2px",
    border: "1px solid var(--mantine-color-gray-5)",
    borderRight: "none",
    color: "var(--mantine-color-gray-7)",
    position: "static",
    marginRight: 0,
  },
  input: {
    height: 36,
    flex: 1,
    borderRadius: "0 2px 2px 0",
    border: "1px solid var(--mantine-color-gray-5)",
    background: "#FFF",
    color: "var(--mantine-color-dark-9)",
    paddingLeft: 12,
  },
} as const;

import { ProcedureCode } from "../types/fee-table";
import { EditFeesFormSchema, EditFeesFormType } from "../zod-schemas/edit-fees";

interface EditFeesDrawerProps {
  opened: boolean;
  onClose: () => void;
  procedure: ProcedureCode | null;
  onSave: (procedure: ProcedureCode, values: EditFeesFormType) => Promise<void>;
}

function getInitialValues(procedure: ProcedureCode | null): EditFeesFormType {
  return {
    insuranceFee: procedure?.insuranceFee ?? null,
    selfPayFee: procedure?.selfPayFee ?? null,
    isActive: procedure?.isActive ?? true,
  };
}

export default function EditFeesDrawer({
  opened,
  onClose,
  procedure,
  onSave,
}: EditFeesDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo(() => getInitialValues(procedure), [procedure]);

  const form = useForm<EditFeesFormType>({
    initialValues,
    validate: zodResolver(EditFeesFormSchema),
  });

  useEffect(() => {
    if (opened && procedure) {
      const values = getInitialValues(procedure);
      form.setValues(values);
      form.resetDirty(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, procedure]);

  const handleSubmit = async (values: EditFeesFormType) => {
    if (!procedure) return;

    try {
      setIsSubmitting(true);
      await onSave(procedure, values);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    replaceFormSnapshotForDrawerDismiss(form, getInitialValues(null));
    onClose();
  };

  const hasChanges = form.isDirty();

  return (
    <Drawer.Root
      trapFocus
      size={500}
      opened={opened}
      position="right"
      onClose={handleClose}
      closeOnEscape={false}
      closeOnClickOutside={false}
    >
      <Drawer.Overlay />
      <Drawer.Content bg="white" data-testid="edit-fees-drawer">
        <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
          <Drawer.Header h={64} px="md" bg="gray.0">
            <Flex w="100%" gap="md" align="center" justify="space-between">
              <Flex gap="md" align="center" style={{ flex: 1 }}>
                <Drawer.CloseButton
                  aria-label="Close Edit Fees Drawer"
                  data-testid="edit-fees-drawer-close-button"
                />
                <Drawer.Title style={{ flex: 1 }}>
                  <TextWrapper fz="lg" c="dark.9" variant="bold">
                    Edit Fees
                  </TextWrapper>
                </Drawer.Title>
              </Flex>
              <DisabledReasonButton
                type="submit"
                disabled={isSubmitting}
                data-testid="edit-fees-drawer-save-button"
                disabledReason={hasChanges ? undefined : NO_CHANGES_REASON}
              >
                Save
              </DisabledReasonButton>
            </Flex>
          </Drawer.Header>
          <Drawer.Body p={24}>
            <Stack gap={24} align="center">
              <TextWrapper w="100%" c="gray.6" variant="bold">
                {procedure?.name}
              </TextWrapper>

              <NumberInput
                w="100%"
                hideControls
                leftSection="$"
                placeholder="0"
                decimalScale={2}
                label="Insurance Fee"
                leftSectionWidth={49}
                allowNegative={false}
                thousandSeparator=","
                disabled={isSubmitting}
                styles={currencyInputStyles}
                leftSectionPointerEvents="none"
                data-testid="edit-fees-drawer-insurance-fee-input"
                {...form.getInputProps("insuranceFee")}
              />

              <NumberInput
                w="100%"
                hideControls
                leftSection="$"
                placeholder="0"
                decimalScale={2}
                label="Self Pay Fee"
                leftSectionWidth={49}
                allowNegative={false}
                thousandSeparator=","
                disabled={isSubmitting}
                styles={currencyInputStyles}
                leftSectionPointerEvents="none"
                data-testid="edit-fees-drawer-self-pay-fee-input"
                {...form.getInputProps("selfPayFee")}
              />

              <Group
                p="md"
                w="100%"
                justify="space-between"
                style={{ border: "1px solid var(--mantine-color-gray-3)", borderRadius: 8 }}
              >
                <Group gap="xs">
                  <TextWrapper c="dark.9" variant="bold">
                    Status
                  </TextWrapper>
                  <TextWrapper
                    variant="bold"
                    c={form.values.isActive ? "green.7" : "gray.6"}
                    bg={form.values.isActive ? "green.0" : "gray.1"}
                  >
                    {form.values.isActive ? "Active" : "Inactive"}
                  </TextWrapper>
                </Group>
                <Switch
                  disabled={isSubmitting}
                  checked={form.values.isActive}
                  aria-label="Toggle procedure status"
                  data-testid="edit-fees-drawer-status-toggle"
                  onChange={(event) => form.setFieldValue("isActive", event.currentTarget.checked)}
                />
              </Group>
            </Stack>
          </Drawer.Body>
        </form>
      </Drawer.Content>
    </Drawer.Root>
  );
}
